import _ from 'lodash';
import jsSHA from 'jssha';

import { LOGS } from '../config/Logs.js';
import { PATTERNS } from '../config/Patterns.js';
import { appStore as store } from '../stores/store';
import { impersonationEndedDestination } from '../config/Impersonation';

let sessionId = null;
let sessionName = null;

const URL = process.env.REACT_APP_URL;
const URL_SUFFIX = process.env.REACT_APP_URL_SUFFIX;
const API_KEY = process.env.REACT_APP_API_KEY;
const API_SECRET = process.env.REACT_APP_API_SECRET;
const LAYER_API_URL = 'https://accounting-api.minute7.com/';

export const setSession = (newSessionId, newSessionName) => {
    sessionId = newSessionId;
    sessionName = newSessionName;
};

export const addSessionParams = paramsObject => {
    if (sessionId !== null && sessionName !== null) {
        paramsObject[sessionName] = sessionId;
    }
    return paramsObject;
};

// Shared 401/403 handling for baseRequest and baseRequestForLayer. Both request
// paths had a byte-identical copy of this block; they are one function now so a
// change cannot land in only one of them.
//
// Three distinct outcomes, and keeping them distinct is the point (M7-477):
//
//   1. Impersonation ended. A lapsed impersonation session (idle/absolute/
//      revoke/flag-off) returns `{ ended: true, location }` on either status
//      (M7-384). Obey the backend's destination. Checked first, so it keeps
//      working for 401 and 403 alike.
//   2. HTTP 403 — the session is valid, the ACTION was refused. Surface it and
//      leave the user logged in.
//   3. HTTP 401 — the session itself is gone. Log out, as before.
//
// Previously 403 fell through to the 401 branch, so an authorization denial
// ejected the user to /login under the message "your session had expired."
// M7-440 made the users endpoint return a real 403, which turned that latent
// bug into a live one: a user without `manage_users` who tried to add a user
// was logged out and told their session had expired (M7-498).
const handleAuthFailureResponse = response =>
    response
        .json()
        .catch(() => ({}))
        .then(body => {
            const endedDestination = impersonationEndedDestination(body);
            if (endedDestination) {
                window.location.href = endedDestination;
            } else if (response.status === 403) {
                store.dispatch({ type: 'API_REQUEST_FORBIDDEN_ERROR' });
            } else {
                store.dispatch({ type: 'API_REQUEST_UNAUTHORIZED_ERROR' });
            }
            const error = new Error(response.statusText);
            error.response = response;
            throw error;
        });

const toDataURL = url =>
    fetch(url)
        .then(response => response.blob())
        .then(
            blob =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                })
        );

export function handleImageDownload({ url, name }) {
    toDataURL(url).then(dataUrl => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = name;
        a.click();
    });
}
/**
 * Creates a file object with name, filename, from a base64 data string
 * @param {string} dataURL a data*base64 file string of the file to create
 */
function dataURLtoFile(dataURL) {
    let arr = dataURL.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    let file = new Blob([u8arr], { type: mime });
    return file;
}

export function postRequestWithFormData(requestData, route, paramsObject, headers = {}) {
    const formData = new FormData();

    for (let key in requestData) {
        if (typeof requestData[key] === 'undefined' || requestData[key] === null) {
            continue;
        }

        if (key === 'new_attachments') {
            requestData[key].forEach(attachment => {
                // See if we need to convert base64 string
                if (typeof attachment.path === 'string' && !attachment.path.includes('https://')) {
                    formData.append(`${key}[]`, dataURLtoFile(attachment.path), attachment.name);
                } else {
                    formData.append(`${key}[]`, attachment.path);
                }
            });
        } else if (key === 'deleted_attachments') {
            requestData[key].forEach(attachment => {
                formData.append(`${key}[]`, JSON.stringify(attachment));
            });
        } else if (key === 'logo') {
            if (requestData[key].path !== null && requestData[key].path !== '') {
                formData.append(
                    `${key}[]`,
                    dataURLtoFile(requestData[key].path),
                    requestData[key].name
                );
            }
        } else if (requestData[key] instanceof Array) {
            requestData[key].forEach(element => {
                formData.append(`${key}[]`, element);
            });
        } else if (requestData[key] instanceof Object) {
            for (let item in requestData[key]) {
                formData.append(`${key}[${item}]`, requestData[key][item]);
            }
        } else {
            if (requestData[key] === null) {
                requestData[key] = '';
            }
            formData.append(key, requestData[key].toString());
        }
    }

    if (sessionId !== null && sessionName !== null) {
        paramsObject[sessionName] = sessionId;
    }

    const params = {
        method: 'POST',
        credentials: 'include',
        body: formData,
        headers: headers,
    };

    return baseRequest(paramsObject, route, params);
}

export function postRequestWithFormDataForLayer(
    requestData,
    route,
    paramsObject,
    headers = { 'Access-Control-Allow-Credentials': true }
) {
    const formData = new FormData();

    for (let key in requestData) {
        if (typeof requestData[key] === 'undefined') {
            continue;
        }

        if (key === 'new_attachments') {
            requestData[key].forEach(attachment => {
                // See if we need to convert base64 string
                if (typeof attachment.path === 'string' && !attachment.path.includes('https://')) {
                    formData.append(`${key}[]`, dataURLtoFile(attachment.path), attachment.name);
                } else {
                    formData.append(`${key}[]`, attachment.path);
                }
            });
        } else if (key === 'deleted_attachments') {
            requestData[key].forEach(attachment => {
                formData.append(`${key}[]`, JSON.stringify(attachment));
            });
        } else if (key === 'logo') {
            if (requestData[key].path !== null && requestData[key].path !== '') {
                formData.append(
                    `${key}[]`,
                    dataURLtoFile(requestData[key].path),
                    requestData[key].name
                );
            }
        } else if (requestData[key] instanceof Array) {
            requestData[key].forEach(element => {
                formData.append(`${key}[]`, element);
            });
        } else if (requestData[key] instanceof Object) {
            for (let item in requestData[key]) {
                formData.append(`${key}[${item}]`, requestData[key][item]);
            }
        } else {
            if (requestData[key] === null) {
                requestData[key] = '';
            }
            formData.append(key, requestData[key].toString());
        }
    }

    if (sessionId !== null && sessionName !== null) {
        paramsObject[sessionName] = sessionId;
    }

    const params = {
        method: 'POST',
        body: formData,
        headers: headers,
    };

    return baseRequestForLayer(paramsObject, route, params);
}

export function getData(paramsObject, route) {
    if (sessionId !== null && sessionName !== null) {
        paramsObject[sessionName] = sessionId;
    }

    const params = {
        credentials: 'include',
    };
    return baseRequest(paramsObject, route, params);
}

function createQueryParamsString(paramsObject) {
    const strArr = [];
    for (const key in paramsObject) {
        if (paramsObject.hasOwnProperty(key) && typeof key !== 'object') {
            const encodedKey = encodeURI(key).replace(/%20/g, '+');
            const encodedValue = encodeURI(paramsObject[key]).replace(/%20/g, '+');
            strArr.push(`${encodedKey}=${encodedValue}`);
        }
    }
    return strArr.join('&');
}

function encodeURI(str) {
    return encodeURIComponent(str).replace(
        PATTERNS.encodeURIChars,
        c =>
            `%${c
                .charCodeAt(0)
                .toString(16)
                .toUpperCase()}`
    );
}

export function createQueryString(paramsObject, route) {
    let tempParamsObject = JSON.parse(JSON.stringify(paramsObject));
    tempParamsObject.z_ca = Math.floor(Math.random() * 100000);
    tempParamsObject.api_key = API_KEY;
    tempParamsObject = sortParams(tempParamsObject);

    const signature = `${API_SECRET + URL_SUFFIX + route}?${createQueryParamsString(
        tempParamsObject
    )}`;
    const shaObject = new jsSHA('SHA-512', 'TEXT');
    shaObject.update(signature);
    tempParamsObject.signature = shaObject.getHash('HEX');

    if (LOGS.ENABLE_LOGS) {
        console.info(`${URL + URL_SUFFIX + route}?${createQueryParamsString(tempParamsObject)}`);
    }
    return `${URL + URL_SUFFIX + route}?${createQueryParamsString(tempParamsObject)}`;
}

export function createQueryStringForLayer(paramsObject, route) {
    let tempParamsObject = JSON.parse(JSON.stringify(paramsObject));
    tempParamsObject.z_ca = Math.floor(Math.random() * 100000);
    tempParamsObject.api_key = API_KEY;
    tempParamsObject = sortParams(tempParamsObject);

    const signature = `${API_SECRET + URL_SUFFIX + route}?${createQueryParamsString(
        tempParamsObject
    )}`;
    const shaObject = new jsSHA('SHA-512', 'TEXT');
    shaObject.update(signature);
    tempParamsObject.signature = shaObject.getHash('HEX');

    if (LOGS.ENABLE_LOGS) {
        console.info(`${LAYER_API_URL + route}?${createQueryParamsString(tempParamsObject)}`);
    }
    return `${LAYER_API_URL + route}?${createQueryParamsString(tempParamsObject)}`;
}

function sortParams(paramsObject) {
    const keys = [];
    for (const i in paramsObject) {
        keys.push(i);
    }
    keys.sort();
    const sortedParams = {};
    for (let i = 0; i < keys.length; i++) {
        sortedParams[keys[i]] = paramsObject[keys[i]];
    }
    return sortedParams;
}

function baseRequest(paramsObject, route, params) {
    const urlWithParams = createQueryString(paramsObject, route);

    if (LOGS.ENABLE_LOGS) {
        console.info(`paramsObject =====> `, paramsObject);
        console.info(`route =====> `, route);
        console.info(`params =====> `, params);
        console.info(`urlWithParams =====> `, urlWithParams);
    }

    return fetch(urlWithParams, params)
        .then(response => {
            if (response.status >= 200 && response.status < 300) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return response
                        .json()
                        .then(json => {
                            if (json.m7 && json.m7.error) {
                                json.error = json.m7.error;
                            }
                            json.status = response.status;

                            if (json.error) {
                                const error = new Error(json.error);
                                error.response = response;
                                throw error;
                            }

                            if (LOGS.ENABLE_LOGS) {
                                console.info(`Response from route ${route}: `, json);
                            }
                            return json;
                        })
                        .catch(error => {
                            console.info(
                                `ERROR: while parsing response from route ${route}: `,
                                error
                            );
                            error.response = response;
                            throw error;
                        });
                } else {
                    return response.text().then(text => {
                        if (text.match(/^{/)) {
                            // attempt to parse as JSON anyway
                            try {
                                const json = JSON.parse(text);
                                return json;
                            } catch (e) {
                                return text;
                            }
                        }
                        return text;
                    });
                }
            } else if (response.status === 401 || response.status === 403) {
                return handleAuthFailureResponse(response);
            } else if (response.status === 400 || response.status === 422) {
                // M7-482: 422 (Unprocessable Entity) is a validation rejection,
                // the same class as 400 -- parse the JSON body so the server's
                // `errors` message reaches the caller as error.message instead
                // of a bare "Unprocessable Entity" statusText. The self-service
                // password-change guard is the first endpoint to return 422 on
                // this SPA; no prior caller depended on the old behaviour. Only
                // baseRequest is changed (baseRequestForLayer has no 422 caller).
                return response
                    .json()
                    .then(res => {
                        const error = new Error(messageFromErrors(res));
                        error.response = response;
                        throw error;
                    })
                    .catch(error => {
                        if (LOGS.ENABLE_LOGS) {
                            console.info(`ERROR: In baseRequest when on route ${route}: `, error);
                        }

                        throw error;
                    });
            } else {
                const error = new Error(response.statusText);
                error.response = response;
                throw error;
            }
        })
        .catch(error => {
            if (LOGS.ENABLE_LOGS) {
                console.info(`ERROR: In baseRequest when on route ${route}: `, error);
            }

            throw error;
        });
}

// M7-482: normalize a validation-error body into a single display string. The
// server's `errors` may be a string or an array of strings (e.g.
// ["Current password is incorrect"]); coercing an array straight through
// `new Error(...)` would comma-join without spaces, and a non-string/array
// shape would stringify to "[object Object]". Join arrays with spaces and fall
// back to `error` for anything unexpected.
function messageFromErrors(res) {
    const errors = res && res.errors;
    if (Array.isArray(errors)) {
        return errors.join(' ');
    }
    if (typeof errors === 'string' && errors.length > 0) {
        return errors;
    }
    return res && res.error;
}

function baseRequestForLayer(paramsObject, route, params) {
    const urlWithParams = createQueryStringForLayer(paramsObject, route);

    if (LOGS.ENABLE_LOGS) {
        console.info(`paramsObject =====> `, paramsObject);
        console.info(`route =====> `, route);
        console.info(`params =====> `, params);
        console.info(`urlWithParams =====> `, urlWithParams);
    }

    return fetch(urlWithParams, params)
        .then(response => {
            if (response.status >= 200 && response.status < 300) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return response
                        .json()
                        .then(json => {
                            if (json.m7 && json.m7.error) {
                                json.error = json.m7.error;
                            }
                            json.status = response.status;

                            if (json.error) {
                                const error = new Error(json.error);
                                error.response = response;
                                throw error;
                            }

                            if (LOGS.ENABLE_LOGS) {
                                console.info(`Response from route ${route}: `, json);
                            }
                            return json;
                        })
                        .catch(error => {
                            console.info(
                                `ERROR: while parsing response from route ${route}: `,
                                error
                            );
                            error.response = response;
                            throw error;
                        });
                } else {
                    return response.text().then(text => {
                        if (text.match(/^{/)) {
                            // attempt to parse as JSON anyway
                            try {
                                const json = JSON.parse(text);
                                return json;
                            } catch (e) {
                                return text;
                            }
                        }
                        return text;
                    });
                }
            } else if (response.status === 401 || response.status === 403) {
                return handleAuthFailureResponse(response);
            } else if (response.status === 400) {
                return response
                    .json()
                    .then(res => {
                        const error = new Error(res.errors || res.error);
                        error.response = response;
                        throw error;
                    })
                    .catch(error => {
                        if (LOGS.ENABLE_LOGS) {
                            console.info(`ERROR: In baseRequest when on route ${route}: `, error);
                        }

                        throw error;
                    });
            } else {
                const error = new Error(response.statusText);
                error.response = response;
                throw error;
            }
        })
        .catch(error => {
            if (LOGS.ENABLE_LOGS) {
                console.info(`ERROR: In baseRequest when on route ${route}: `, error);
            }

            throw error;
        });
}

// Extract embedded partial records in responses returned from the API
// For example:
//
// const records = [
//    { id: 1, Customer: { id: 1, name: 'John' }},
//    { id: 2, Customer: { id: 2, name: 'Jane' }},
// ];
//
// const extracted = extractEmbeddedEntities(['Customer'], records);
//
// // extracted is now:
// { Customer: [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }] }

export const extractEmbeddedEntities = (entityNames, records) => {
    const results = entityNames.reduce((all, name) => {
        all[name] = { entities: [], byId: {} };
        return all;
    }, {});

    records.forEach(record => {
        entityNames.forEach(name => {
            if (
                record[name] &&
                !(record[name].id in results[name].byId) &&
                record[name].id !== null
            ) {
                results[name].entities.push(record[name]);
                results[name].byId[record[name].id] = true;
            }
        });
    });

    return _.mapValues(results, 'entities');
};
